import React, { useState, useEffect } from "react";
import Select from "react-select";
import axios from "axios";
import { Modal, Button } from "react-bootstrap";
import sparkling from "./sparkling.png";
import "bootstrap/dist/css/bootstrap.min.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Title= () => {
  const [topic, setTopic] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tone, setTone] = useState(null);
  const [customTone, setCustomTone] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [country, setCountry] = useState(null);
  const [countries, setCountries] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [toneOptions, setToneOptions] = useState([
    { label: "Neutral", value: "neutral", icon: "🤖" },
    { label: "Formal", value: "formal", icon: "🎩" },
    { label: "Friendly", value: "friendly", icon: "😊" },
    { label: "Casual", value: "casual", icon: "😎" },
    { label: "Professional", value: "professional", icon: "💼" },
    { label: "Diplomatic", value: "diplomatic", icon: "🤝" },
    { label: "Confident", value: "confident", icon: "💪" },
    { label: "Academic", value: "academic", icon: "🎓" },
    { label: "Vivid", value: "vivid", icon: "🎨" },
    { label: "Custom...", value: "custom", icon: "⚙️" },
  ]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await axios.get("https://countries.dev/countries");
        const countryData = response.data.map((country) => ({
          label: country.name,
          value: country.name,
          languages: Object.values(country.languages || {}).map((lang) => lang.name || lang).join(", "),
          flag: country.flags?.svg || country.flags?.png || "",
        }));
        console.log("[countries.dev] sample record:", response.data?.[0]);
        setCountries(countryData);
      } catch (error) {
        console.error("Error fetching country data:", error?.response?.status, error?.response?.data || error.message);
        toast.error("Failed to fetch country data.");
      }
    };

    const fetchApiKeys = async () => {
      try {
        const response = await axios.get("https://ytubetools.com/api/openaiKey", { timeout: 5000 });
        if (response.status !== 200) {
          throw new Error(`Unexpected response status: ${response.status}`);
        }
        const activeKeys = response.data.filter((key) => key.active);
        if (activeKeys.length === 0) {
          toast.error("No active API keys available.");
        } else {
          setApiKeys(activeKeys);
        }
      } catch (error) {
        console.error("Error fetching API keys:", error.message);
        toast.error(`Error fetching API keys: ${error.message}`);
      }
    };

    fetchCountries();
    fetchApiKeys();
  }, []);

  const handleGenerateResults = async () => {
    if (!topic.trim()) {
      setError("Please write a topic before generating titles.");
      return;
    }
    if (!country) {
      setError("Please select a language.");
      return;
    }
    if (!tone) {
      setError("Please select a tone.");
      return;
    }
  
    setError("");
    setLoading(true);
  
    const language = country.languages || "English";

    try {
      const response = await axios.post("https://tools.videoters.com/api/unified", {
        model: "gpt-5.5",
        messages: [
          {
            role: "system",
            content: `You are an assistant that generates 10 SEO-friendly titles for the topic "${topic}". Titles should be written in ${language} with a ${tone.label || tone} tone.`,
          },
          {
            role: "user",
            content: `Generate exactly 10 distinct and meaningful SEO-friendly titles for the topic "${topic}". Ensure each title is unique and captures attention. Return the titles separated by "###".`,
          },
        ],
        temperature: 0.7,
        max_tokens: 1000,
      });

      if (response.data && response.data.choices && response.data.choices.length > 0) {
        const generatedText = response.data.choices[0].message.content.trim();

        // Split the results into 10 titles using "###"
        let splitResults = generatedText
          .split("###")
          .map((result) => result.trim())
          .filter((result) => result);

        // Ensure exactly 10 titles
        if (splitResults.length < 10) {
          while (splitResults.length < 10) {
            splitResults.push(`Title ${splitResults.length + 1}: Placeholder title.`);
          }
        } else if (splitResults.length > 10) {
          splitResults = splitResults.slice(0, 10);
        }

        setResults(splitResults);
      } else {
        toast.error("No results found in the API response.");
      }
    } catch (error) {
      console.error("[Unified API Error]", error.response?.data || error.message);
      toast.error(`Error: ${error.response?.data?.error?.message || error.message}`);
    }

    setLoading(false);
  };
  






  const handleCustomToneSave = () => {
    if (customTone.trim()) {
      const customOption = { label: customTone, value: customTone.toLowerCase(), icon: "⚙️" };
      toneOptions.push(customOption);
      setTone(customOption);
      setShowModal(false);
      setCustomTone("");
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success("Copied to clipboard!", {
          position: "top-right",
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: true,
          theme: "colored",
        });
      })
      .catch(() => {
        toast.error("Failed to copy!", {
          position: "top-right",
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: true,
          theme: "colored",
        });
      });
  };

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "48px",
      border: "none",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(255, 89, 55, 0.12)" : "none",
      borderRadius: "5px",
      backgroundColor: "#ffffff",
      cursor: "pointer",
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "2px 11px",
    }),
    placeholder: (base) => ({
      ...base,
      color: "#8b93a3",
      fontSize: "15px",
    }),
    singleValue: (base) => ({
      ...base,
      color: "#232a3b",
      fontSize: "15px",
    }),
    indicatorSeparator: (base) => ({
      ...base,
      backgroundColor: "#d8dce4",
    }),
    dropdownIndicator: (base, state) => ({
      ...base,
      color: state.isFocused ? "#ff5937" : "#adb3bf",
      padding: "10px 12px",
      "&:hover": { color: "#ff5937" },
    }),
    menu: (base) => ({
      ...base,
      zIndex: 20,
      overflow: "hidden",
      border: "1px solid #e3e6ec",
      borderRadius: "10px",
      boxShadow: "0 14px 35px rgba(31, 43, 71, 0.14)",
    }),
    option: (base, state) => ({
      ...base,
      padding: "10px 12px",
      color: state.isSelected ? "#ffffff" : "#232a3b",
      backgroundColor: state.isSelected
        ? "#ff5937"
        : state.isFocused
          ? "#fff0eb"
          : "#ffffff",
      cursor: "pointer",
    }),
  };

  const wordCount = topic.trim() ? topic.trim().split(/\s+/).length : 0;

  return (
    <div className="ai-title-page">
      <style>{`
        .ai-title-page,
        .ai-title-page * {
          box-sizing: border-box;
        }

        .ai-title-page {
          --title-navy: #1e2b4a;
          --title-orange: #ff5937;
          --title-orange-dark: #ef4828;
          --title-text: #4c5260;
          --title-border: #d8dde7;
          min-height: 100vh;
          width: 100%;
          padding: 70px 24px 80px;
          background: #fff9f7;
          color: var(--title-navy);
          font-family: Inter, "Segoe UI", Arial, sans-serif;
        }

        .ai-title-shell {
          width: min(1120px, 100%);
          margin: 0 auto;
        }

        .ai-title-hero {
          max-width: 1080px;
          margin: 0 auto 64px;
          text-align: center;
        }

        .ai-title-hero h1 {
          margin: 0 0 18px;
          color: var(--title-navy);
          font-size: clamp(42px, 6vw, 76px);
          font-weight: 800;
          line-height: 1.08;
          letter-spacing: -2.5px;
        }

        .ai-title-hero h1 span {
          color: var(--title-orange);
        }

        .ai-title-hero p {
          max-width: 960px;
          margin: 0 auto;
          color: #505463;
          font-size: clamp(16px, 1.75vw, 21px);
          line-height: 1.55;
        }

        .ai-title-card {
          padding: 38px 32px 32px;
          border: 1px solid #dce1e9;
          border-radius: 24px;
          background: rgba(255, 255, 255, 0.78);
          box-shadow: 0 12px 32px rgba(28, 42, 72, 0.04);
        }

        .ai-title-field {
          position: relative;
          border: 1px solid #cfd5df;
          border-radius: 5px;
          background: #ffffff;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }

        .ai-title-field:focus-within {
          border-color: var(--title-orange);
          box-shadow: 0 0 0 3px rgba(255, 89, 55, 0.1);
        }

        .ai-title-field-label {
          position: absolute;
          top: -10px;
          left: 12px;
          z-index: 2;
          margin: 0;
          padding: 0 4px;
          background: #ffffff;
          color: #151a29;
          font-size: 14px;
          font-weight: 700;
          line-height: 20px;
        }

        .ai-title-required {
          color: var(--title-orange);
        }

        .ai-title-topic {
          margin-bottom: 38px;
        }

        .ai-title-topic textarea {
          display: block;
          width: 100%;
          min-height: 126px;
          padding: 14px 15px 30px;
          resize: vertical;
          border: 0;
          border-radius: 5px;
          outline: 0;
          background: transparent;
          color: #232a3b;
          font: inherit;
          font-size: 16px;
          line-height: 1.6;
        }

        .ai-title-topic textarea::placeholder {
          color: #929bac;
        }

        .ai-title-counter {
          position: absolute;
          right: 12px;
          bottom: 7px;
          color: #8d96a8;
          font-size: 12px;
          line-height: 1;
          pointer-events: none;
        }

        .ai-title-select-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 28px;
          margin-bottom: 32px;
        }

        .ai-title-select-field {
          min-width: 0;
          padding: 1px;
        }

        .ai-title-error {
          margin: -12px 0 24px;
          padding: 11px 14px;
          border: 1px solid #ffd1c8;
          border-radius: 8px;
          background: #fff0ec;
          color: #d9391a;
          font-size: 14px;
        }

        .ai-title-generate {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          min-height: 60px;
          padding: 14px 24px;
          border: 0;
          border-radius: 999px;
          background: var(--title-orange);
          box-shadow: 0 7px 12px rgba(255, 89, 55, 0.2);
          color: #ffffff;
          font-size: 17px;
          font-weight: 700;
          transition: transform 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
        }

        .ai-title-generate:hover:not(:disabled) {
          transform: translateY(-1px);
          background: var(--title-orange-dark);
          box-shadow: 0 10px 18px rgba(255, 89, 55, 0.26);
        }

        .ai-title-generate:focus-visible {
          outline: 3px solid rgba(255, 89, 55, 0.3);
          outline-offset: 3px;
        }

        .ai-title-generate:disabled {
          cursor: wait;
          opacity: 0.72;
        }

        .ai-title-generate img {
          filter: brightness(0) invert(1);
          object-fit: contain;
        }

        .ai-title-results {
          margin-top: 34px;
        }

        .ai-title-results-heading {
          margin: 0 0 18px;
          color: var(--title-navy);
          font-size: 22px;
          font-weight: 800;
        }

        .ai-title-results-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 16px;
        }

        .ai-title-result-card {
          display: flex;
          flex-direction: column;
          min-height: 174px;
          padding: 20px;
          border: 1px solid #e1e5ec;
          border-radius: 14px;
          background: #ffffff;
          box-shadow: 0 7px 22px rgba(31, 43, 71, 0.05);
        }

        .ai-title-result-title {
          margin: 0 0 8px;
          color: var(--title-navy);
          font-size: 16px;
          font-weight: 800;
          line-height: 1.45;
        }

        .ai-title-result-description {
          margin: 0 0 6px;
          color: #3f4655;
          font-size: 14px;
          line-height: 1.65;
        }

        .ai-title-result-details {
          margin: 0;
          color: #747d8e;
          font-size: 13px;
          line-height: 1.6;
        }

        .ai-title-copy {
          align-self: flex-start;
          margin-top: auto;
          padding: 7px 15px;
          border: 1px solid var(--title-orange);
          border-radius: 999px;
          background: #ffffff;
          color: var(--title-orange);
          font-size: 13px;
          font-weight: 700;
          transition: color 0.2s ease, background 0.2s ease;
        }

        .ai-title-copy:hover {
          background: var(--title-orange);
          color: #ffffff;
        }

        .ai-title-modal .modal-content {
          overflow: hidden;
          border: 0;
          border-radius: 16px;
          box-shadow: 0 20px 55px rgba(25, 37, 64, 0.2);
        }

        .ai-title-modal .modal-title {
          color: #1e2b4a;
          font-weight: 800;
        }

        .ai-title-modal .form-control:focus {
          border-color: var(--title-orange);
          box-shadow: 0 0 0 3px rgba(255, 89, 55, 0.12);
        }

        .ai-title-modal-save {
          border-color: #ff5937 !important;
          background: #ff5937 !important;
        }

        @media (max-width: 767px) {
          .ai-title-page {
            padding: 46px 16px 56px;
          }

          .ai-title-hero {
            margin-bottom: 38px;
          }

          .ai-title-hero h1 {
            font-size: clamp(38px, 12vw, 54px);
            letter-spacing: -1.5px;
          }

          .ai-title-card {
            padding: 30px 18px 22px;
            border-radius: 18px;
          }

          .ai-title-select-grid,
          .ai-title-results-grid {
            grid-template-columns: 1fr;
          }

          .ai-title-select-grid {
            gap: 25px;
          }
        }
      `}</style>

      <ToastContainer />

      <div className="ai-title-shell">
       

        <main className="ai-title-card">
          <div className="ai-title-field ai-title-topic">
            <label className="ai-title-field-label" htmlFor="titleTopic">
              Topic / Keywords <span className="ai-title-required">*</span>
            </label>
            <textarea
              id="titleTopic"
              placeholder="e.g. Digital Marketing Strategies 2025..."
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              rows={5}
              maxLength={500}
            />
            <span className="ai-title-counter">
              {wordCount} words&nbsp;&nbsp; {topic.length}/500 chars
            </span>
          </div>

          <div className="ai-title-select-grid">
            <div className="ai-title-field ai-title-select-field">
              <label className="ai-title-field-label" htmlFor="toneSelect">
                Tone <span className="ai-title-required">*</span>
              </label>
              <Select
                inputId="toneSelect"
                options={toneOptions}
                value={tone}
                placeholder="Select tone..."
                styles={selectStyles}
                onChange={(selectedOption) =>
                  selectedOption.value === "custom"
                    ? setShowModal(true)
                    : setTone(selectedOption)
                }
                getOptionLabel={({ label, icon }) => (
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <span>{icon}</span>
                    {label}
                  </div>
                )}
              />
            </div>

            <div className="ai-title-field ai-title-select-field">
              <label className="ai-title-field-label" htmlFor="countrySelect">
                Language <span className="ai-title-required">*</span>
              </label>
              <Select
                inputId="countrySelect"
                options={countries}
                placeholder="Select language..."
                styles={selectStyles}
                onChange={(selected) => setCountry(selected)}
                getOptionLabel={({ label, flag, languages }) => (
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <img
                      src={flag}
                      alt={label}
                      style={{ width: "20px", height: "15px", objectFit: "cover" }}
                    />
                    <div>
                      <div>{label}</div>
                      <small style={{ color: "#6c757d" }}>{languages}</small>
                    </div>
                  </div>
                )}
              />
            </div>
          </div>

          {error && <div className="ai-title-error">{error}</div>}

          <button
            type="button"
            onClick={handleGenerateResults}
            className="ai-title-generate"
            disabled={loading}
          >
            <img src={sparkling} alt="" width={18} height={18} aria-hidden="true" />
            {loading ? "Generating Titles..." : "Generate Titles"}
          </button>

          {results.length > 0 && (
            <section className="ai-title-results">
              <h2 className="ai-title-results-heading">Generated Titles</h2>
              <div className="ai-title-results-grid">
                {results.map((result, index) => {
                  // Split the result into the acronym and its description
                  const [acronymPart, ...descriptionParts] = result.split(":");
                  const acronym = acronymPart.trim(); // The acronym itself
                  const description = descriptionParts.join(":").trim(); // The rest as the description

                  return (
                    <article key={index} className="ai-title-result-card">
                      <p className="ai-title-result-title">
                        {index + 1}. {acronym}
                      </p>

                      <p className="ai-title-result-description">
                        {description.split(".")[0]}.
                      </p>

                      <p className="ai-title-result-details">
                        {description.split(".").slice(1).join(".").trim()}
                      </p>

                      <button
                        type="button"
                        className="ai-title-copy"
                        onClick={() => handleCopy(result)}
                      >
                        Copy
                      </button>
                    </article>
                  );
                })}
              </div>
            </section>
          )}
        </main>
      </div>

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        dialogClassName="ai-title-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Custom Tone</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <input
            type="text"
            className="form-control"
            placeholder="Enter custom tone"
            value={customTone}
            onChange={(e) => setCustomTone(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button className="ai-title-modal-save" onClick={handleCustomToneSave}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Title;
